import React, { useState } from 'react';
import { Box, Typography, TextField, FormControlLabel, Checkbox, ToggleButton, ToggleButtonGroup, IconButton } from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Check as CheckIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import {
  MULTI_SELECT_TOGGLE_SX,
  SINGLE_SELECT_TOGGLE_SX,
  LAST_MONTH_TOGGLE_BUTTON_SX,
  DELETE_ICON_SPACER_WIDTH,
  BOOKMARK_ICON_SPACER_WIDTH,
} from './sections.style';
import { LastMonthReadonlyPanel } from './LastMonthFieldToggle';
import { SectionFieldDTO } from '../magic-report.type';
import { DateFieldRenderer } from './field-renderers/DateFieldRenderer';
import { NumberFieldRenderer } from './field-renderers/NumberFieldRenderer';
import { SelectFieldRenderer } from './field-renderers/SelectFieldRenderer';
import { TextArrayFieldRenderer } from './field-renderers/TextArrayFieldRenderer';
import { FieldRow } from './FieldRow';
import { LastMonthFieldToggle } from './LastMonthFieldToggle';
import { useMagicReport } from '../context/MagicReportContext';
import { getNextActionItemOrder } from './action-items.util';
import { FieldEntryDTO } from '../magic-report.type';

/**
 * Field configuration for declarative rendering
 */
export interface FieldConfig {
  fieldName: string;
  type:
    | 'date'
    | 'number'
    | 'text'
    | 'text-array'
    | 'select'
    | 'checkbox'
    | 'readonly'
    | 'multiselect-toggle'
    | 'text-paired'
    | 'text-with-refresh'
    | 'readonly-calculated'
    | 'custom-component';
  label: string;
  options?: string[]; // For select fields and toggle buttons
  optionLabels?: Record<string, string>; // For multiselect-toggle: display text per option (stored value stays the option itself)
  defaultValue?: string; // Optional default value for new entries (e.g., text-array)
  customLabel?: React.ReactNode; // For custom label rendering
  boldLabel?: boolean; // For checkbox labels that need emphasis
  showBookmark?: boolean; // Whether to show bookmark toggle (defaults to true)
  disabled?: boolean; // Whether the input should be disabled
  inlineLayout?: boolean; // For select fields: if true, label appears after toggle buttons in same row
  hideLabel?: boolean; // Only wired up for type: 'text' — suppresses its label heading. No effect on other types.

  // For multiselect-toggle and text-paired fields
  pairedWith?: string; // fieldName of the paired content field
  getDefaultText?: (selectedValues: string[]) => string; // Function to generate default text
  autoSelectRules?: { option: string; autoSelect: string[] }; // Auto-select rules
  isOptionDisabled?: (option: string, selectedValues: string[]) => boolean; // Function to determine if an option should be disabled

  // For readonly fields: format the stored value before display
  formatValue?: (value: string) => string;

  // For number fields: format on blur (e.g. bracket negatives); raw value is still stored
  formatOnBlur?: (value: string) => string;
  // For number fields: hide browser spin buttons
  hideSpinners?: boolean;

  // For text-with-refresh field
  onRefresh?: () => void; // Callback for refresh button
  // For text-with-refresh field: also flags the field as an error (red outline + helper text)
  // if the current value still contains this substring (e.g. an unfilled "XXXXX" placeholder),
  // in addition to the existing exact-match-with-defaultValue check.
  errorIfContains?: string;

  // For readonly-calculated field
  calculateValue?: () => string; // Function to calculate display value
  getValueColor?: () => string; // Function to determine value color
  mutuallyExclusive?: [string, string][];

  /**
   * For multiselect-toggle: an independent single-select toggle rendered immediately to the LEFT of
   * the main button group, sharing its row (no separate label or row of its own). Its value lives in
   * its own field, so it stays distinct from the main group's selections.
   */
  leadingToggle?: {
    fieldName: string;
    options: string[];
    /** Value assumed when the field is empty. */
    defaultValue: string;
  };

  // For custom-component field: receives the current field state and a change callback.
  // Zero-argument form (() => ReactNode) is also supported for static components that don't need field access.
  renderComponent?:
    | ((field: SectionFieldDTO | undefined, onChange: (updates: Partial<SectionFieldDTO>) => void) => React.ReactNode)
    | (() => React.ReactNode);
}

/**
 * Layout configuration for flexible rendering
 */
export interface DeclarativeLayoutConfig {
  leftColumn?: FieldConfig[];
  rightColumn?: FieldConfig[];
  bottomSection?: FieldConfig[];
  singleColumn?: FieldConfig[]; // For single-column layouts
}

interface DeclarativeLayoutRendererProps {
  layout: DeclarativeLayoutConfig;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  onDeleteField?: (fieldName: string) => void; // Optional delete handler
  onBook?: () => number;
}

/**
 * Unified declarative renderer for all sections
 * Renders fields based on configuration instead of manual JSX
 */
export const DeclarativeLayoutRenderer: React.FC<DeclarativeLayoutRendererProps> = ({
  layout,
  getField,
  handleFieldChange,
  onDeleteField,
  onBook,
}) => {
  const { reportState } = useMagicReport();
  const getActionItemOrder = onBook ?? (() => getNextActionItemOrder(reportState.sections));
  /**
   * Parse multi-select toggle value (comma-separated)
   */
  const parseToggleValue = (value: string): string[] => {
    return value ? value.split(',').filter(Boolean) : [];
  };

  /**
   * Handle toggle button group changes with auto-select rules
   */
  const handleToggleGroupChange = (fieldConfig: FieldConfig, newValues: string[]) => {
    const field = getField(fieldConfig.fieldName);
    if (!field) return;

    const currentValues = parseToggleValue(field.value || '');

    // Determine which values were added
    const addedValues = newValues.filter(v => !currentValues.includes(v));

    const removedValues = currentValues.filter(v => !newValues.includes(v));

    // Start with a fresh copy
    let finalValues = [...newValues];

    // Apply mutually exclusive rules first
    if (fieldConfig.mutuallyExclusive?.length) {
      fieldConfig.mutuallyExclusive.forEach(([a, b]) => {
        if (finalValues.includes(a) && finalValues.includes(b)) {
          // Keep whichever was clicked last
          const last = addedValues.includes(a) ? a : addedValues.includes(b) ? b : null;
          if (last === a) finalValues = finalValues.filter(v => v !== b);
          else if (last === b) finalValues = finalValues.filter(v => v !== a);
        }
      });
    }

    // Apply auto-select rules (like “Notarized” → also “Signed”)
    addedValues.forEach(addedValue => {
      if (fieldConfig.autoSelectRules && addedValue === fieldConfig.autoSelectRules.option) {
        fieldConfig.autoSelectRules.autoSelect.forEach(additionalOption => {
          if (!finalValues.includes(additionalOption)) {
            finalValues.push(additionalOption);
          }
        });
      }
    });

    // Apply reverse auto-unselect rule (e.g. unselecting Signed removes Notarized)
    removedValues.forEach(removedValue => {
      if (fieldConfig.autoSelectRules && fieldConfig.autoSelectRules.autoSelect.includes(removedValue)) {
        // If this removed value was one of the "auto-selected" dependencies,
        // also remove the triggering option
        const triggerOption = fieldConfig.autoSelectRules.option;
        finalValues = finalValues.filter(v => v !== triggerOption);
      }
    });

    // Apply conditional validation rules (e.g., Signed requires Architect Reviewed)
    if (fieldConfig.isOptionDisabled) {
      finalValues = finalValues.filter(value => !fieldConfig.isOptionDisabled!(value, finalValues));
    }

    // Update the toggle field
    handleFieldChange(fieldConfig.fieldName, { value: finalValues.join(',') });

    // Update the paired content field if exists
    if (fieldConfig.pairedWith && fieldConfig.getDefaultText) {
      const defaultText = fieldConfig.getDefaultText(finalValues);
      handleFieldChange(fieldConfig.pairedWith, { value: defaultText });
    }
  };

  /**
   * Render a single field based on its type
   */
  const renderField = (fieldConfig: FieldConfig) => {
    const field = getField(fieldConfig.fieldName);

    switch (fieldConfig.type) {
      case 'date':
        return (
          <DateFieldRenderer
            key={fieldConfig.fieldName}
            value={field?.value}
            addToActionItems={field?.addToActionItems}
            onChange={(value, addToActionItems) => handleFieldChange(fieldConfig.fieldName, { value: value || '', addToActionItems })}
            fieldName={fieldConfig.fieldName}
            label={fieldConfig.label}
            showBookmark={fieldConfig.showBookmark}
          />
        );

      case 'number':
        return (
          <NumberFieldRenderer
            key={fieldConfig.fieldName}
            value={field?.value}
            addToActionItems={field?.addToActionItems}
            onChange={(value, addToActionItems) => handleFieldChange(fieldConfig.fieldName, { value, addToActionItems })}
            fieldName={fieldConfig.fieldName}
            label={fieldConfig.label}
            showBookmark={fieldConfig.showBookmark}
            disabled={fieldConfig.disabled}
            formatOnBlur={fieldConfig.formatOnBlur}
            hideSpinners={fieldConfig.hideSpinners}
          />
        );

      case 'select':
        if (fieldConfig.inlineLayout) {
          // Inline layout: label after toggle buttons in same row
          return (
            <Box
              key={fieldConfig.fieldName}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}
            >
              {fieldConfig.customLabel || (
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {fieldConfig.label}
                </Typography>
              )}
              <SelectFieldRenderer
                value={field?.value}
                addToActionItems={field?.addToActionItems}
                onChange={(value, addToActionItems) => handleFieldChange(fieldConfig.fieldName, { value, addToActionItems })}
                fieldName={fieldConfig.fieldName}
                options={fieldConfig.options || []}
                showBookmark={fieldConfig.showBookmark}
              />
            </Box>
          );
        }
        // Default layout: label above toggle buttons
        return (
          <Box key={fieldConfig.fieldName}>
            {fieldConfig.customLabel || (
              <Typography variant='body2' sx={{ mb: 0.5, fontWeight: 500 }}>
                {fieldConfig.label}
              </Typography>
            )}
            <SelectFieldRenderer
              value={field?.value}
              addToActionItems={field?.addToActionItems}
              onChange={(value, addToActionItems) => handleFieldChange(fieldConfig.fieldName, { value, addToActionItems })}
              fieldName={fieldConfig.fieldName}
              options={fieldConfig.options || []}
              showBookmark={fieldConfig.showBookmark}
            />
          </Box>
        );

      case 'readonly': {
        const rawValue = field?.value || '';
        const readonlyDisplayValue = fieldConfig.formatValue ? fieldConfig.formatValue(rawValue) : rawValue;
        const isNegativeFormatted = readonlyDisplayValue.startsWith('(') && readonlyDisplayValue.endsWith(')');
        return (
          <FieldRow
            key={fieldConfig.fieldName}
            addToActionItems={field?.addToActionItems}
            onBookmarkToggle={() => {
              if (field) handleFieldChange(fieldConfig.fieldName, { addToActionItems: !field.addToActionItems });
            }}
            showBookmark={fieldConfig.showBookmark}
          >
            <TextField
              fullWidth
              label={fieldConfig.label}
              value={readonlyDisplayValue}
              InputProps={{
                readOnly: true,
                sx: isNegativeFormatted ? { color: 'error.main', '& input': { color: 'error.main' } } : undefined,
              }}
              InputLabelProps={{ shrink: true }}
            />
          </FieldRow>
        );
      }

      case 'checkbox':
        return (
          <FieldRow
            key={fieldConfig.fieldName}
            addToActionItems={field?.addToActionItems}
            onBookmarkToggle={() => {
              if (field) handleFieldChange(fieldConfig.fieldName, { addToActionItems: !field.addToActionItems });
            }}
            alignStart
            showBookmark={fieldConfig.showBookmark}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={field?.value === 'true'}
                  onChange={e => handleFieldChange(fieldConfig.fieldName, { value: e.target.checked ? 'true' : 'false' })}
                />
              }
              label={fieldConfig.label}
              sx={{
                m: 0,
                alignItems: 'center',
                display: 'flex',
                '.MuiFormControlLabel-label': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  ...(fieldConfig.boldLabel && { fontWeight: 600 }),
                },
              }}
            />
          </FieldRow>
        );

      case 'text': {
        const isDefaultText = Boolean(fieldConfig.defaultValue) && (field?.value || '') === fieldConfig.defaultValue;
        return (
          <Box key={fieldConfig.fieldName} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Label heading */}
            {!fieldConfig.hideLabel && (
              <Typography variant='body2' color='text' fontWeight='medium' textTransform='capitalize'>
                {fieldConfig.label}
              </Typography>
            )}
            {/* LastMonth toggle and content - in its own container */}
            <LastMonthFieldToggle
              lastMonthValue={field?.lastMonth}
              label={fieldConfig.label}
              reserveTrailingActionSpace={Boolean(onDeleteField)}
            />

            <FieldRow
              addToActionItems={field?.addToActionItems}
              onBookmarkToggle={() => {
                if (field) handleFieldChange(fieldConfig.fieldName, { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              showBookmark={fieldConfig.showBookmark}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={20}
                  value={field?.value || ''}
                  onChange={e => handleFieldChange(fieldConfig.fieldName, { value: e.target.value })}
                  placeholder={`Enter ${fieldConfig.label.toLowerCase()}...`}
                  error={isDefaultText}
                  helperText={isDefaultText ? 'Default text — please update with correct details.' : undefined}
                  sx={
                    isDefaultText
                      ? {
                          '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                          '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        }
                      : undefined
                  }
                />
                {onDeleteField && (
                  <IconButton
                    size='small'
                    onClick={() => onDeleteField(fieldConfig.fieldName)}
                    sx={{ color: 'error.main', mt: 0.5 }}
                    title='Clear content'
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </FieldRow>
          </Box>
        );
      }

      case 'text-array': {
        const [showLastMonth, setShowLastMonth] = useState(false);

        // Separate last month entries from current entries
        const allEntries = field?.entries || [];
        const lastMonthEntries = allEntries.filter(entry => entry.isFromLastMonth === true);
        const currentEntries = allEntries.filter(entry => !entry.isFromLastMonth);

        const handleAddEntry = () => {
          const defaultValue =
            // Prefer a field-config default (section-specific) if provided
            fieldConfig.defaultValue ??
            // Otherwise, use the first current entry as a template
            (currentEntries.length > 0 && currentEntries[0].value ? currentEntries[0].value : '');
          const newEntry: FieldEntryDTO = {
            value: defaultValue,
            addToActionItems: false,
            order: allEntries.length,
          };
          // Merge with last month entries when adding
          handleFieldChange(fieldConfig.fieldName, { entries: [...allEntries, newEntry] });
        };

        const handleToggleShowLastMonth = () => {
          setShowLastMonth(!showLastMonth);
        };

        return (
          <Box key={fieldConfig.fieldName} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Label heading with plus icon and last month toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2' color='text' fontWeight='medium' textTransform='capitalize'>
                {fieldConfig.label}
              </Typography>
              <IconButton
                size='small'
                onClick={handleAddEntry}
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  width: 28,
                  height: 28,
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  },
                }}
                title='Add new entry'
              >
                <AddIcon fontSize='small' />
              </IconButton>
              {lastMonthEntries.length > 0 && (
                <Box
                  onClick={handleToggleShowLastMonth}
                  sx={{
                    ...LAST_MONTH_TOGGLE_BUTTON_SX,
                    color: showLastMonth ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: showLastMonth ? 'primary.dark' : 'text.primary',
                    },
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      width: '0.8rem',
                      height: '0.8rem',
                    }}
                  />
                  <Typography
                    variant='caption'
                    sx={{
                      fontWeight: 600,
                      textTransform: 'none',
                      letterSpacing: 0.2,
                      color: 'inherit',
                    }}
                  >
                    {showLastMonth ? 'Hide last month' : 'Show last month'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Show last month entries when toggled */}
            {showLastMonth && lastMonthEntries.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {lastMonthEntries.map((entry, index) => (
                  <Box
                    key={`last-month-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        position: 'relative',
                      }}
                    >
                      <LastMonthReadonlyPanel value={entry.value || ''} />
                    </Box>

                    {/* Invisible spacers to match [delete] [bookmark] order in TextArrayFieldRenderer */}
                    <Box sx={{ width: DELETE_ICON_SPACER_WIDTH, flexShrink: 0, pt: 0.75, visibility: 'hidden' }} />
                    <Box sx={{ width: BOOKMARK_ICON_SPACER_WIDTH, flexShrink: 0, pt: 0.75 }} />
                  </Box>
                ))}
              </Box>
            )}

            {/* Current entries only */}
            <TextArrayFieldRenderer
              entries={currentEntries}
              onChange={(entries: FieldEntryDTO[]) => {
                // Merge current entries with last month entries when updating
                const allEntries = [...lastMonthEntries, ...entries];
                handleFieldChange(fieldConfig.fieldName, { entries: allEntries });
              }}
              fieldName={fieldConfig.fieldName}
              getNextActionItemOrder={getActionItemOrder}
              showAddButton={false}
            />
          </Box>
        );
      }

      case 'multiselect-toggle': {
        // Render toggle button group (for status selection)
        const selectedValues = parseToggleValue(field?.value || '');
        return (
          <Box key={fieldConfig.fieldName}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant='body2' color='text' fontWeight='medium' textTransform='capitalize'>
                {fieldConfig.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Independent single-select toggle sharing this row, to the left of the main group */}
                {fieldConfig.leadingToggle && (
                  <ToggleButtonGroup
                    exclusive
                    size='small'
                    value={getField(fieldConfig.leadingToggle.fieldName)?.value || fieldConfig.leadingToggle.defaultValue}
                    onChange={(_, newValue) => {
                      // MUI passes null when the active button is clicked again — keep the current value
                      if (newValue == null) return;
                      handleFieldChange(fieldConfig.leadingToggle!.fieldName, { value: newValue as string });
                    }}
                    sx={SINGLE_SELECT_TOGGLE_SX}
                  >
                    {fieldConfig.leadingToggle.options.map(option => (
                      <ToggleButton key={option} value={option}>
                        {option}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
                <ToggleButtonGroup
                  value={selectedValues}
                  size='small'
                  onChange={(_, newValues) => {
                    // MUI passes null when all are deselected
                    const values = (newValues as string[]) || [];
                    handleToggleGroupChange(fieldConfig, values);
                  }}
                  sx={MULTI_SELECT_TOGGLE_SX}
                >
                  {(fieldConfig.options || []).map(option => {
                    const isDisabled = fieldConfig.isOptionDisabled ? fieldConfig.isOptionDisabled(option, selectedValues) : false;
                    return (
                      <ToggleButton key={option} value={option} disabled={isDisabled} sx={{ gap: 0.75 }}>
                        {selectedValues.includes(option) && <CheckIcon sx={{ fontSize: 16 }} />}
                        <Box component='span'>{fieldConfig.optionLabels?.[option] ?? option}</Box>
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              </Box>
            </Box>
            {/* LastMonth toggle and content - below the main field */}
            <LastMonthFieldToggle lastMonthValue={field?.lastMonth} label={fieldConfig.label} />
          </Box>
        );
      }

      case 'text-paired':
        // Render content field paired with toggle buttons
        return (
          <Box key={fieldConfig.fieldName}>
            <Box sx={{ mb: 0.5 }}>
              <LastMonthFieldToggle
                lastMonthValue={field?.lastMonth}
                label={fieldConfig.label}
                reserveTrailingActionSpace={Boolean(onDeleteField)}
              />
            </Box>
            <FieldRow
              addToActionItems={field?.addToActionItems}
              onBookmarkToggle={() => {
                if (field) handleFieldChange(fieldConfig.fieldName, { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              showBookmark={fieldConfig.showBookmark}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={20}
                  value={field?.value || ''}
                  onChange={e => handleFieldChange(fieldConfig.fieldName, { value: e.target.value })}
                  placeholder={`Enter ${fieldConfig.label.toLowerCase()}...`}
                />
                {onDeleteField && (
                  <IconButton
                    size='small'
                    onClick={() => onDeleteField(fieldConfig.fieldName)}
                    sx={{ color: 'error.main', mt: 0.5 }}
                    title='Clear content'
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </FieldRow>
          </Box>
        );

      case 'text-with-refresh': {
        // Render text field with a refresh button and heading label (similar to 'text' type)
        const isDefaultText = Boolean(fieldConfig.defaultValue) && (field?.value || '') === fieldConfig.defaultValue;
        const hasUnfilledPlaceholder = Boolean(fieldConfig.errorIfContains) && (field?.value || '').includes(fieldConfig.errorIfContains!);
        const showError = isDefaultText || hasUnfilledPlaceholder;
        return (
          <Box key={fieldConfig.fieldName} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant='body2' color='text' fontWeight='medium' textTransform='capitalize'>
              {fieldConfig.label}
            </Typography>
            {/* LastMonth toggle and content - in its own container */}
            <LastMonthFieldToggle
              lastMonthValue={field?.lastMonth}
              label={fieldConfig.label}
              reserveTrailingActionSpace={Boolean(fieldConfig.onRefresh)}
            />
            <FieldRow
              addToActionItems={field?.addToActionItems}
              onBookmarkToggle={() => {
                if (field) handleFieldChange(fieldConfig.fieldName, { addToActionItems: !field.addToActionItems });
              }}
              alignStart
              sx={{ mt: 0.5 }}
              showBookmark={fieldConfig.showBookmark}
              trailingAction={
                fieldConfig.onRefresh ? (
                  <Box
                    component='span'
                    onClick={fieldConfig.onRefresh}
                    title='Restore boilerplate text'
                    sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex' }}
                  >
                    <RefreshIcon fontSize='small' />
                  </Box>
                ) : undefined
              }
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={20}
                value={field?.value || ''}
                onChange={e => handleFieldChange(fieldConfig.fieldName, { value: e.target.value })}
                placeholder={`Enter ${fieldConfig.label.toLowerCase()}...`}
                variant='outlined'
                error={showError}
                helperText={showError ? 'Default text — please update with actual details.' : undefined}
                sx={{
                  bgcolor: 'background.paper',
                  ...(showError && {
                    '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                  }),
                }}
              />
            </FieldRow>
          </Box>
        );
      }

      case 'readonly-calculated':
        // Render calculated readonly field using NumberFieldRenderer for consistent styling
        return (
          <NumberFieldRenderer
            key={fieldConfig.fieldName}
            value={fieldConfig.calculateValue ? fieldConfig.calculateValue() : field?.value || ''}
            addToActionItems={false}
            onChange={() => undefined}
            fieldName={fieldConfig.fieldName}
            label={fieldConfig.label}
            showBookmark={fieldConfig.showBookmark}
            disabled
            valueColor={fieldConfig.getValueColor ? fieldConfig.getValueColor() : undefined}
          />
        );

      case 'custom-component': {
        const onChange = (updates: Partial<SectionFieldDTO>) => handleFieldChange(fieldConfig.fieldName, updates);
        const rendered = fieldConfig.renderComponent
          ? fieldConfig.renderComponent.length === 0
            ? (fieldConfig.renderComponent as () => React.ReactNode)()
            : (fieldConfig.renderComponent as (f: SectionFieldDTO | undefined, cb: typeof onChange) => React.ReactNode)(field, onChange)
          : null;
        return <React.Fragment key={fieldConfig.fieldName}>{rendered}</React.Fragment>;
      }

      default:
        return null;
    }
  };

  /**
   * Render a column of fields
   */
  const renderColumn = (fields: FieldConfig[]) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields.map(fieldConfig => (
          <Box key={fieldConfig.fieldName} data-field-name={fieldConfig.fieldName}>
            {renderField(fieldConfig)}
          </Box>
        ))}
      </Box>
    );
  };

  // Single column layout - use gap from parent container, not nested gap
  if (layout.singleColumn) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {layout.singleColumn.map(fieldConfig => renderField(fieldConfig))}
      </Box>
    );
  }

  // Two-column grid layout
  return (
    <>
      {(layout.leftColumn || layout.rightColumn) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          {/* Left Column */}
          {layout.leftColumn && renderColumn(layout.leftColumn)}

          {/* Right Column */}
          {layout.rightColumn && renderColumn(layout.rightColumn)}
        </Box>
      )}

      {/* Bottom Section (full width) */}
      {layout.bottomSection && layout.bottomSection.length > 0 && (
        <Box sx={{ mt: 3 }}>{layout.bottomSection.map(fieldConfig => renderField(fieldConfig))}</Box>
      )}
    </>
  );
};
